/**
 * /admin/revenue — NoFee Platform Revenue Dashboard.
 *
 * Headline metrics (MRR, ARR, ARPU, Active count, Trialing count,
 * Past-due, Canceled, Churn rate) + a daily growth time series for the
 * last 7/30/90 days + a "Recent subscriptions" live feed.
 *
 * Auth: requires the same admin key as the rest of /admin (stored in
 * localStorage as `nofee_admin_key`). Cross-checked against the
 * `NOFEE_ADMIN_KEY` env var on the server.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  TrendingUp,
  DollarSign,
  Users,
  AlertTriangle,
  Activity,
  Sparkles,
  CalendarRange,
  RefreshCw,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_KEY_LS = 'nofee_admin_key';

const cents = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Stat = ({ Icon, label, value, hint, tone = 'gold', testid }) => {
  const tones = {
    gold: { ring: 'ring-yellow-500/30', icon: 'text-yellow-300', bg: 'bg-yellow-500/10' },
    emerald: { ring: 'ring-emerald-500/30', icon: 'text-emerald-300', bg: 'bg-emerald-500/10' },
    rose: { ring: 'ring-rose-500/30', icon: 'text-rose-300', bg: 'bg-rose-500/10' },
    sky: { ring: 'ring-sky-500/30', icon: 'text-sky-300', bg: 'bg-sky-500/10' },
  };
  const t = tones[tone];
  return (
    <div
      data-testid={testid}
      className={`p-5 rounded-2xl border border-white/8 bg-gradient-to-br from-zinc-950 to-black ring-1 ${t.ring}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-9 h-9 rounded-xl ${t.bg} ${t.icon} flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </span>
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-bold">{label}</p>
      </div>
      <p className="text-[26px] font-black text-white tracking-tight leading-none">{value}</p>
      {hint && <p className="mt-1.5 text-[11.5px] text-white/45">{hint}</p>}
    </div>
  );
};

const RevenueAdminPage = () => {
  const [key, setKey] = useState(() => {
    try { return localStorage.getItem(ADMIN_KEY_LS) || ''; } catch { return ''; }
  });
  const [keyInput, setKeyInput] = useState(key);
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/admin/revenue?days=${windowDays}`, {
        headers: { 'X-Admin-Key': key },
      });
      setData(r.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        setError('Admin key rejected. Check the value in your .env or try again.');
        try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
      } else {
        setError(err?.response?.data?.detail || 'Could not load revenue data.');
      }
    } finally {
      setLoading(false);
    }
  }, [key, windowDays]);

  useEffect(() => { load(); }, [load]);

  const onSaveKey = (e) => {
    e?.preventDefault?.();
    if (!keyInput.trim()) return;
    try { localStorage.setItem(ADMIN_KEY_LS, keyInput.trim()); } catch {}
    setKey(keyInput.trim());
  };

  // ─── Key gate ────────────────────────────────────────────────────────
  if (!key) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4" data-testid="revenue-admin-keygate">
        <form
          onSubmit={onSaveKey}
          className="w-full max-w-md p-6 rounded-3xl border border-yellow-500/30 bg-gradient-to-br from-zinc-950 to-black"
        >
          <h1 className="text-xl font-bold mb-2">NoFee Admin · Revenue</h1>
          <p className="text-[12.5px] text-white/55 mb-4">Enter your admin key to unlock the platform revenue dashboard.</p>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Admin key"
            data-testid="revenue-admin-key-input"
            className="w-full bg-zinc-900 border border-zinc-800 focus:border-yellow-500 rounded-xl px-4 py-3 text-[14px] outline-none"
          />
          <button
            type="submit"
            data-testid="revenue-admin-key-submit"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[14px]"
          >
            Unlock dashboard
          </button>
        </form>
      </div>
    );
  }

  const h = data?.headline || {};
  const series = (data?.series || []).map((row) => ({
    ...row,
    new_mrr_usd: (row.new_mrr_cents || 0) / 100,
    cumulative_mrr_usd: (row.cumulative_mrr_cents || 0) / 100,
    short: row.date.slice(5),  // MM-DD
  }));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="revenue-admin-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-md border-b border-white/[0.06]">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/admin"
              data-testid="revenue-admin-back"
              className="w-9 h-9 inline-flex items-center justify-center rounded-xl border border-white/10 hover:border-white/30 text-white/75 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="min-w-0">
              <p className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> NoFee Admin
              </p>
              <h1 className="text-[16px] font-bold tracking-tight text-white truncate">Platform revenue</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:inline-flex items-center gap-1 rounded-xl border border-white/10 bg-white/[0.03] p-1">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setWindowDays(d)}
                  data-testid={`revenue-window-${d}`}
                  className={`px-3 py-1.5 rounded-lg text-[11.5px] font-bold transition-all ${
                    windowDays === d
                      ? 'bg-yellow-500/15 text-yellow-200 border border-yellow-500/40'
                      : 'text-white/65 hover:text-white'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="revenue-admin-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 hover:border-white/30 text-white/75 hover:text-white text-[12px] font-bold"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-5 sm:px-8 py-8 space-y-8" data-testid="revenue-admin-main">
        {error && (
          <div data-testid="revenue-admin-error" className="p-4 rounded-2xl border border-rose-500/30 bg-rose-500/[0.06] text-rose-200 text-[13.5px] flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
          </div>
        )}

        {/* Headline metrics */}
        <section data-testid="revenue-headline" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat
            testid="revenue-mrr"
            Icon={DollarSign}
            label="MRR"
            value={cents(h.mrr_cents)}
            hint={`ARR ${cents(h.arr_cents)}`}
            tone="gold"
          />
          <Stat
            testid="revenue-active"
            Icon={Users}
            label="Active customers"
            value={h.active_count ?? 0}
            hint={`${h.trialing_count || 0} on trial`}
            tone="emerald"
          />
          <Stat
            testid="revenue-arpu"
            Icon={TrendingUp}
            label="ARPU"
            value={cents(h.arpu_cents)}
            hint="Avg revenue / active sub"
            tone="sky"
          />
          <Stat
            testid="revenue-churn"
            Icon={AlertTriangle}
            label={`Churn (${data?.window_days || windowDays}d)`}
            value={`${h.churn_rate_pct ?? 0}%`}
            hint={`${h.cancellations_in_window || 0} cancellations`}
            tone="rose"
          />
        </section>

        {/* Time series — cumulative MRR + daily new subs */}
        <section className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
          <div data-testid="revenue-cumulative-mrr-card" className="p-5 rounded-3xl border border-white/8 bg-gradient-to-br from-zinc-950 to-black">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold flex items-center gap-1.5">
                  <Activity className="w-3 h-3" /> Cumulative MRR
                </p>
                <h3 className="mt-1 text-lg font-bold tracking-tight">Last {data?.window_days || windowDays} days</h3>
              </div>
              <CalendarRange className="w-4 h-4 text-white/35" />
            </div>
            <div className="h-64" data-testid="revenue-mrr-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f5c542" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#f5c542" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#222" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="short" tick={{ fill: '#888', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#888', fontSize: 11 }} axisLine={false} tickLine={false} width={50} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 12, color: '#fff' }}
                    formatter={(v) => [`$${Number(v).toLocaleString()}`, 'Cumulative MRR']}
                    labelFormatter={(l) => `Day ${l}`}
                  />
                  <Area type="monotone" dataKey="cumulative_mrr_usd" stroke="#f5c542" strokeWidth={2.2} fill="url(#mrrFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div data-testid="revenue-new-subs-card" className="p-5 rounded-3xl border border-white/8 bg-gradient-to-br from-zinc-950 to-black">
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-300 font-bold flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Daily new subscriptions
            </p>
            <h3 className="mt-1 mb-3 text-lg font-bold tracking-tight">Acquisition rate</h3>
            <div className="h-64" data-testid="revenue-newsubs-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#222" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="short" tick={{ fill: '#888', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#888', fontSize: 11 }} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 12, color: '#fff' }}
                    formatter={(v) => [v, 'New subs']}
                  />
                  <Bar dataKey="new_subs" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* Status breakdown */}
        <section data-testid="revenue-status-breakdown" className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ['Active', h.active_count, 'emerald'],
            ['Trialing', h.trialing_count, 'gold'],
            ['Past due', h.past_due_count, 'rose'],
            ['Canceled', h.canceled_count, 'sky'],
          ].map(([label, n, tone]) => (
            <div
              key={label}
              data-testid={`revenue-status-${label.toLowerCase().replace(/\s/g, '-')}`}
              className="p-4 rounded-2xl border border-white/8 bg-white/[0.02]"
            >
              <p className="text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-bold">{label}</p>
              <p className={`mt-1.5 text-2xl font-black tracking-tight ${
                tone === 'emerald' ? 'text-emerald-300' :
                tone === 'gold' ? 'text-yellow-300' :
                tone === 'rose' ? 'text-rose-300' : 'text-sky-300'
              }`}>{n ?? 0}</p>
            </div>
          ))}
        </section>

        {/* Recent subscriptions */}
        <section data-testid="revenue-recent-feed" className="p-5 rounded-3xl border border-white/8 bg-gradient-to-br from-zinc-950 to-black">
          <p className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold mb-3 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Recent subscriptions
          </p>
          {data?.recent_subscriptions?.length ? (
            <ul className="space-y-2">
              {data.recent_subscriptions.map((s, i) => (
                <li key={i} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-white/[0.06] bg-white/[0.02]">
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-bold text-white truncate">{s.restaurant_name || '—'}</p>
                    <p className="text-[11.5px] text-white/45 truncate">{s.plan || 'NoFee Ordering'} · {s.subscribed_at?.slice(0, 10) || '—'}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[13.5px] font-bold text-yellow-300">{cents(s.amount_cents)}</p>
                    <p className={`text-[10.5px] uppercase tracking-[0.18em] font-bold ${
                      s.status === 'active' ? 'text-emerald-300' :
                      s.status === 'trialing' ? 'text-yellow-300' :
                      s.status === 'past_due' ? 'text-rose-300' : 'text-white/45'
                    }`}>{s.status || 'unknown'}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12.5px] text-white/45">No subscriptions yet — your dashboard will populate here as restaurants subscribe.</p>
          )}
        </section>
      </main>
    </div>
  );
};

export default RevenueAdminPage;
